"use client";

import styles from "./SignUpCreatePasswordStep.module.scss";
import { useForm, usePasswordValidation } from "@/hooks";
import {
  setCurrentStep,
  setFieldValue } from "@/stores";
import {
  RedXCircleIcon,
  SmallArrowLeftIcon,
  GreenCheckCircleIcon,
} from "@/components";

import { useTranslations } from "@/i18n";

import { useCreatePasswordForm } from "../../_hooks";
import {
  Flex,
  Input,
  Button,
  Fieldset,
} from "@vetty/ui";

/**
 * SignUpCreatePasswordStep Component
 *
 * Fourth step of the sign-up process where users create their password.
 * Handles password validation, requirements checking, and user registration.
 */
export const SignUpCreatePasswordStep = () => {
  const t = useTranslations("signUp.form");

  // Form state and submission logic
  const { errors, values, loading } = useForm();

  // Password validation using shared hook
  const { passwordChecks, passwordRequirements } = usePasswordValidation(
    String(values.password ?? "")
  );

  // Password creation form state and submission logic
  const { handleInputChange, handleFormSubmit } = useCreatePasswordForm();

  /**
   * Handles input change with field value update
   */
  const handleInputChangeWithValue = (field: string, value: string) => {
    setFieldValue(field, value);
    handleInputChange(field, value);
  };

  /**
   * Navigates back to previous step
   */
  const handleBackToPrevStep = () => {
    setCurrentStep(1);
  };

  return (
    <Flex direction={"column"} className={styles["comp"]}>
      {/* Back navigation */}
      <div onClick={handleBackToPrevStep} className={styles["comp__back"]}>
        <SmallArrowLeftIcon />
        <span>{t("back")}</span>
      </div>

      {/* User email from account-details step (form state) */}
      <Flex className={styles["comp__signing-as"]} gap={5}>
        <div>{t("signingUpAs")}</div>
        <strong>{values.email ?? ""}</strong>
      </Flex>

      {/* Password creation form */}
      <Flex direction={"column"} gap={24}>
        {/* Password input with requirements */}
        <Fieldset isError={errors.password} errorMessage={errors.password}>
          <Flex direction={"column"} gap={8}>
            <Input
              required
              type={"password"}
              id={"password"}
              label={t("password")}
              onChange={(v) => handleInputChangeWithValue("password", v)}
            />

            {/* Password requirements checklist */}
            <Flex
              direction={"column"}
              gap={10}
              className={styles["comp__requirements"]}
            >
              {Object.entries(passwordRequirements).map(([key, req]) => {
                const passed = passwordChecks[key];
                const Icon = passed ? GreenCheckCircleIcon : RedXCircleIcon;

                return (
                  <Flex key={key} align={"center"} gap={8}>
                    <Icon />
                    <span>{req.label}</span>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Fieldset>

        {/* Repeat password input */}
        <Fieldset
          isError={errors.repeatPassword}
          errorMessage={errors.repeatPassword}
        >
          <Input
            required
            type={"password"}
            id={"repeatPassword"}
            label={t("repeatPassword")}
            onChange={(v) => handleInputChangeWithValue("repeatPassword", v)}
          />
        </Fieldset>

        {/* Submit button */}
        <Button
          disabled={loading}
          loading={loading}
          onClick={() => handleFormSubmit(values)}
          className={styles["comp__submit-button"]}
        >
          {t("getStartedForFree")}
        </Button>
      </Flex>
    </Flex>
  );
};
